import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApiErrorResponse } from '../models/api-response.model';
import { LocalService } from './local.service';

type HttpMethod = 'GET' | 'POST' | 'DELETE';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public rootUrl: string;
  public token: string = '';

  constructor(
    private localService: LocalService,
    private http: HttpClient,
  ) {
    this.rootUrl = environment.APIEndpoint;
  }

  async init() {
    this.token = (await this.localService.getDataV2<string>('token')) || '';
  }

  request<TResponse = any, TParams = any>(
    path: string,
    method: HttpMethod,
    params?: TParams,
  ): Promise<TResponse> {
    if (method === 'GET') {
      return this.doGetRequest<TResponse>(path);
    } else if (method === 'DELETE') {
      return this.doDeleteRequest<TResponse>(path);
    }

    return this.doPostRequest<TResponse, TParams>(path, params);
  }

  private doGetRequest<TResponse>(path: string): Promise<TResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        let headers = new HttpHeaders();
        headers = headers.set('x-access-token', this.token);

        this.http
          .get<TResponse>(`${this.rootUrl}${path}`, { headers })
          .subscribe((result) => {
            if (this.isError(result)) {
              reject(result);
            } else {
              resolve(result);
            }
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  private doDeleteRequest<TResponse>(path: string): Promise<TResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        let headers = new HttpHeaders();
        headers = headers.set('x-access-token', this.token);

        this.http
          .delete<TResponse>(`${this.rootUrl}${path}`, { headers })
          .subscribe((result) => {
            if (this.isError(result)) {
              reject(result);
            } else {
              resolve(result);
            }
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  private doPostRequest<TResponse, TParams>(
    path: string,
    params?: TParams,
  ): Promise<TResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        let headers = new HttpHeaders();

        if (this.token) {
          headers = headers.set('x-access-token', this.token);
        }

        this.http
          .post<TResponse>(`${this.rootUrl}${path}`, params, { headers })
          .subscribe((result) => {
            if (this.isError(result)) {
              reject(result);
            } else {
              resolve(result);
            }
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  uploadFile<TResponse = any>(path: string, file: File): Promise<TResponse> {
    return new Promise(async (resolve, reject) => {
      const formData: FormData = new FormData();
      formData.append('uploadFile', file, file.name);

      let headers = new HttpHeaders();
      headers = headers.set('x-access-token', this.token);
      // headers = headers.set('Content-Type', 'multipart/form-data');
      headers = headers.set('Accept', 'application/json');

      try {
        this.http
          .post<TResponse>(`${this.rootUrl}${path}`, formData, { headers })
          .subscribe((result) => {
            if (this.isError(result)) {
              reject(result);
            } else {
              resolve(result);
            }
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  private isError(response: unknown): response is ApiErrorResponse {
    return !!response && typeof response === 'object' && 'error' in response;
  }

  setToken(token: string) {
    this.token = token;
  }
}
